import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Zap, Brain, Workflow, BarChart3, Cpu, Shield, ArrowRight } from 'lucide-react';

export const AIAutomationSection = () => {
  const [activeTab, setActiveTab] = useState('ai');

  const aiServices = [
    {
      icon: Brain,
      title: "Audit & Accompagnement IA",
      description: "Analyse de vos besoins métier, utilisation stratégique des outils IA (GPT-4, Claude...) pour optimiser vos processus, et formation de votre équipe aux meilleures pratiques.",
      benefits: ["Audit de vos besoins métier", "Formation pratique IA", "Optimisation avec outils IA", "Conseil en bonnes pratiques"]
    },
    {
      icon: Cpu,
      title: "GPT & LLM Intégration",
      description: "Utilisation des derniers modèles d'IA (GPT-4, Claude, etc.) pour créer des solutions sur mesure adaptées à vos besoins spécifiques.",
      benefits: ["Génération de contenus", "Analyse de textes", "Support client IA", "Traitement de requêtes"]
    },
    {
      icon: BarChart3,
      title: "Analyse Prédictive",
      description: "Exploitez le potentiel de vos données avec des modèles IA qui prédisent tendances et comportements pour optimiser vos stratégies.",
      benefits: ["Prévisions de tendances", "Optimisation processus", "Détection d'anomalies", "ROI prédictif"]
    }
  ];

  const automationServices = [
    {
      flow: "API Integration",
      title: "Intégrations n8n Sans Code",
      description: "Connectez vos outils et applications (CRM, email, bases de données, webhooks) sans coder. Des workflows complexes en quelques clics.",
      examples: ["CRM ↔ Email Marketing", "Slack ↔ Notion ↔ Excel", "Formulaires web ↔ Stockage cloud", "API custom ↔ Notifications"]
    },
    {
      flow: "Workflow",
      title: "Automatisation de Processus",
      description: "Éliminées les tâches répétitives : traitement de données, envoi de documents, génération de rapports, synchronisation de bases de données.",
      examples: ["Traitement factures automatique", "Génération de rapports quotidiens", "Synchronisation multi-outils", "Notifications en temps réel"]
    },
    {
      flow: "IA",
      title: "Workflows IA + Automatisation",
      description: "Combinez la puissance de l'IA avec les automatisations : chatbots intelligents, traitement documentaire, extraction de données par IA.",
      examples: ["Chatbot IA + CRM auto-update", "Scan documents ↔ Classification IA", "Email intelligent ↔ Archivage auto", "Support tier-1 IA auto-scaling"]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section className="py-24 px-4 md:px-6 lg:px-8 bg-gradient-to-b from-[#1a1f2b] via-[#181C25] to-[#1a1f2b]">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-montserrat font-bold text-white mb-4">
            IA & Automatisations Intelligentes
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            HTAG Facility vous accompagne dans l'utilisation stratégique de l'IA et des automatisations n8n pour optimiser vos processus métier, réduire les coûts et augmenter votre productivité en Ardennes.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <span className="px-4 py-2 bg-purple-500/10 border border-purple-500/30 rounded-full text-purple-300 text-sm font-semibold">
              AI-Powered Solutions
            </span>
            <span className="px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-300 text-sm font-semibold">
              n8n Workflows
            </span>
            <span className="px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full text-green-300 text-sm font-semibold">
              Gain d'efficacité
            </span>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex flex-col md:flex-row justify-center gap-4 mb-16">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab('ai')}
            className={`px-8 py-4 rounded-lg font-montserrat font-bold transition-all ${
              activeTab === 'ai'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-600/50'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <Brain size={20} />
              Solutions IA
            </span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab('automation')}
            className={`px-8 py-4 rounded-lg font-montserrat font-bold transition-all ${
              activeTab === 'automation'
                ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg shadow-blue-600/50'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <Zap size={20} />
              Automatisations n8n
            </span>
          </motion.button>
        </div>

        {/* IA Services */}
        {activeTab === 'ai' && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
          >
            {aiServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="group relative h-full"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-pink-600 opacity-0 group-hover:opacity-10 rounded-2xl blur-xl transition-opacity duration-300" />
                  
                  <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 group-hover:border-purple-500 rounded-2xl p-8 h-full transition-all duration-300 group-hover:shadow-xl group-hover:shadow-purple-900/50">
                    <div className="inline-flex p-4 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 mb-6">
                      <Icon className="w-6 h-6 text-white" />
                    </div>

                    <h3 className="text-xl font-montserrat font-bold text-white mb-3 group-hover:text-purple-400 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed mb-6">
                      {service.description}
                    </p>

                    <div className="space-y-2 mb-6">
                      {service.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-gray-300 text-sm">
                          <span className="w-1.5 h-1.5 bg-purple-500 rounded-full" />
                          {benefit}
                        </div>
                      ))}
                    </div>

                    <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full w-0 group-hover:w-full transition-all duration-300" />
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Automation Services */}
        {activeTab === 'automation' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
          >
            {automationServices.map((service, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative h-full"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-cyan-600 opacity-0 group-hover:opacity-10 rounded-2xl blur-xl transition-opacity duration-300" />
                
                <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 group-hover:border-blue-500 rounded-2xl p-8 h-full transition-all duration-300 group-hover:shadow-xl group-hover:shadow-blue-900/50">
                  <div className="inline-flex px-4 py-2 rounded-lg bg-blue-600/20 border border-blue-500/30 text-blue-300 text-xs font-bold mb-4">
                    {service.flow}
                  </div>

                  <h3 className="text-xl font-montserrat font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-6">
                    {service.description}
                  </p>

                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-blue-400 mb-3">Exemples :</p>
                    {service.examples.map((example, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-gray-300 text-sm">
                        <ArrowRight size={14} className="mt-0.5 text-blue-500 flex-shrink-0" />
                        <span>{example}</span>
                      </div>
                    ))}
                  </div>

                  <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full w-0 group-hover:w-full transition-all duration-300" />
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* ROI Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-2xl p-8 md:p-12 mb-16"
        >
          <h3 className="text-2xl font-montserrat font-bold text-white mb-8 text-center">
            Résultats Mesurables avec IA & n8n
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { metric: "80-90%", desc: "Réduction temps tâches répétitives" },
              { metric: "+40%", desc: "Augmentation productivité équipe" },
              { metric: "-60%", desc: "Erreurs humaines éliminées" },
              { metric: "24/7", desc: "Workflows autonomes & continus" }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center p-6 rounded-xl bg-gray-800/50 border border-gray-700"
              >
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-2">
                  {item.metric}
                </div>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center p-12 rounded-2xl bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30"
        >
          <h3 className="text-3xl font-montserrat font-bold mb-4 text-white">
            Transformez Votre Entreprise avec IA & Automatisations
          </h3>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            HTAG Facility vous accompagne dans l'implémentation d'IA et d'automatisations n8n adaptées à votre contexte en Ardennes. Gains garantis en efficacité et réduction de coûts.
          </p>
          <a
            href="/?scroll=contact"
            className="inline-flex items-center space-x-3 px-10 py-4 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 hover:shadow-lg hover:shadow-purple-600/50 text-white font-montserrat font-semibold transition-all"
          >
            <span>Démarrer votre projet IA</span>
            <ArrowRight size={20} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default AIAutomationSection;
